import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Layout } from '../../components/Layout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { PhotoUploader, LazyPhotoGallery } from '../../components/PhotoComponents';
import { toast } from 'sonner';
import { Plus, Wrench, Building2, Clock, Calendar, Cog, Image } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WORK_TYPES = [
  { value: 'preventivo', label: 'Preventivo', color: 'emerald' },
  { value: 'correctivo', label: 'Correctivo', color: 'amber' },
  { value: 'averia', label: 'Avería', color: 'rose' },
];

export default function TechWorks() {
  const { user } = useAuth();
  const [works, setWorks] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [machines, setMachines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    company_id: '',
    machine_id: '',
    work_type: 'preventivo',
    description: '',
    date: new Date().toISOString().split('T')[0],
    start_time: '09:00',
    end_time: '10:00',
    technician: '',
    photos: []
  });

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (user?.name) {
      setFormData(prev => ({ ...prev, technician: user.name }));
    }
  }, [user]);

  const fetchData = async () => {
    try {
      const [worksRes, companiesRes, machinesRes] = await Promise.all([
        axios.get(`${API}/works`),
        axios.get(`${API}/companies`),
        axios.get(`${API}/machines`)
      ]);
      setWorks(worksRes.data);
      setCompanies(companiesRes.data);
      setMachines(machinesRes.data);
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.company_id || !formData.description) {
      toast.error('Completa los campos requeridos');
      return;
    }

    try {
      await axios.post(`${API}/works`, {
        ...formData,
        machine_id: formData.machine_id || null
      });
      toast.success('Trabajo registrado correctamente');
      setDialogOpen(false);
      setFormData({
        company_id: '',
        machine_id: '',
        work_type: 'preventivo',
        description: '',
        date: new Date().toISOString().split('T')[0],
        start_time: '09:00',
        end_time: '10:00',
        technician: user?.name || '',
        photos: []
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al registrar trabajo');
    }
  };

  const getTypeBadge = (type) => {
    const option = WORK_TYPES.find(o => o.value === type);
    const colors = {
      emerald: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
      amber: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
      rose: 'bg-rose-500/10 text-rose-400 border-rose-500/20',
    };
    return (
      <span className={`px-2.5 py-1 rounded-full text-xs font-mono uppercase tracking-wider border ${colors[option?.color || 'emerald']}`}>
        {option?.label || type}
      </span>
    );
  };

  const filteredMachines = machines.filter(m => m.company_id === formData.company_id);
  const myWorks = works.filter(w => w.technician === user?.name).slice(0, 20);

  return (
    <Layout title="Registrar Trabajo">
      <div className="space-y-6 animate-fade-in" data-testid="tech-works">
        <div className="flex items-center justify-between">
          <p className="text-slate-400">Registra los trabajos que realizas en cada empresa</p>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-emerald-600 hover:bg-emerald-500 text-white gap-2" data-testid="add-work-btn">
                <Plus className="w-4 h-4" />
                Nuevo Trabajo
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-rajdhani text-xl">Registrar Trabajo Realizado</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label className="text-slate-300">Empresa *</Label>
                  <Select
                    value={formData.company_id || undefined}
                    onValueChange={(v) => setFormData({ ...formData, company_id: v, machine_id: '' })}
                  >
                    <SelectTrigger className="bg-slate-800 border-slate-700 text-white" data-testid="work-company-select">
                      <SelectValue placeholder="Selecciona la empresa" />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700">
                      {companies.map((c) => (
                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="text-slate-300">Máquina</Label>
                  <Select
                    value={formData.machine_id || undefined}
                    onValueChange={(v) => setFormData({ ...formData, machine_id: v === 'none' ? '' : v })}
                    disabled={!formData.company_id}
                  >
                    <SelectTrigger className="bg-slate-800 border-slate-700 text-white" data-testid="work-machine-select">
                      <SelectValue placeholder={formData.company_id ? "Selecciona máquina" : "Primero selecciona empresa"} />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700">
                      <SelectItem value="none">Sin máquina específica</SelectItem>
                      {filteredMachines.map((m) => (
                        <SelectItem key={m.id} value={m.id}>
                          {m.name} {m.model && `(${m.model})`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-slate-300">Tipo de Trabajo *</Label>
                    <Select
                      value={formData.work_type}
                      onValueChange={(v) => setFormData({ ...formData, work_type: v })}
                    >
                      <SelectTrigger className="bg-slate-800 border-slate-700 text-white" data-testid="work-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-800 border-slate-700">
                        {WORK_TYPES.map((t) => (
                          <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-300">Fecha *</Label>
                    <Input
                      type="date"
                      value={formData.date}
                      onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                      className="bg-slate-800 border-slate-700 text-white"
                      data-testid="work-date-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-slate-300">Hora Inicio</Label>
                    <Input
                      type="time"
                      value={formData.start_time}
                      onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                      className="bg-slate-800 border-slate-700 text-white font-mono"
                      data-testid="work-start-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-300">Hora Fin</Label>
                    <Input
                      type="time"
                      value={formData.end_time}
                      onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                      className="bg-slate-800 border-slate-700 text-white font-mono"
                      data-testid="work-end-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-slate-300">Descripción del Trabajo *</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="bg-slate-800 border-slate-700 text-white min-h-[100px]"
                    placeholder="Describe detalladamente el trabajo realizado..."
                    data-testid="work-description-input"
                  />
                </div>

                {/* Photo Upload */}
                <div className="space-y-2">
                  <Label className="text-slate-300 flex items-center gap-2">
                    <Image className="w-4 h-4" />
                    Fotos del Trabajo
                  </Label>
                  <PhotoUploader
                    photos={formData.photos}
                    onChange={(photos) => setFormData({ ...formData, photos })}
                    maxPhotos={5}
                  />
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="ghost" onClick={() => setDialogOpen(false)} className="text-slate-400">
                    Cancelar
                  </Button>
                  <Button type="submit" className="bg-emerald-600 hover:bg-emerald-500" data-testid="save-work-btn">
                    Registrar Trabajo
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* My Recent Works */}
        <Card className="bg-slate-900/50 border-slate-800">
          <div className="p-4 border-b border-slate-800">
            <h3 className="font-rajdhani text-lg font-semibold text-white">Mis Trabajos Recientes</h3>
          </div>
          <div className="p-0">
            {loading ? (
              <div className="p-8 text-center text-slate-500">Cargando...</div>
            ) : myWorks.length === 0 ? (
              <div className="p-8 text-center text-slate-500">
                <Wrench className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No has registrado trabajos todavía</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-800">
                {myWorks.map((work) => (
                  <div key={work.id} className="p-4 hover:bg-slate-800/30 transition-colors">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          {getTypeBadge(work.work_type)}
                          <span className="text-sm text-slate-400 flex items-center gap-1">
                            <Building2 className="w-4 h-4" />
                            {work.company_name}
                          </span>
                          {work.machine_name && (
                            <span className="text-sm text-sky-400 flex items-center gap-1">
                              <Cog className="w-4 h-4" />
                              {work.machine_name}
                            </span>
                          )}
                        </div>
                        <p className="text-white">{work.description}</p>
                        <div className="flex items-center gap-4 text-sm text-slate-500 mt-2">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            {new Date(work.date).toLocaleDateString('es-ES')}
                          </span>
                          <span className="flex items-center gap-1 font-mono">
                            <Clock className="w-4 h-4" />
                            {work.start_time} - {work.end_time}
                          </span>
                        </div>
                        {/* Photos */}
                        {work.photo_count > 0 && (
                          <div className="mt-3">
                            <LazyPhotoGallery id={work.id} type="works" count={work.photo_count} />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Card>
      </div>
    </Layout>
  );
}
